import React from 'react';
import SEO from '../../components/SEO';
import SiteFooter from '../../components/SiteFooter';
import DirectAnswer from '../../components/DirectAnswer';
import { Link } from 'react-router-dom';
import { cosmeticDentistActonPageSEO } from '../../data/seo';
import { 
  getMedicalConditionLD, 
  getMedicalProcedureLD, 
  getBreadcrumbLD,
  getFAQPageLD 
} from '../../data/structuredData';
import { veneersCondition, veneersProcedure, whiteningCondition, whiteningProcedure } from '../../data/medicalSchemas';
import { cosmeticDentistryFAQs } from '../../data/faqs';
import { cosmeticDentistBreadcrumbs } from '../../data/breadcrumbs';
import { Button } from '../../components/ui/button';
import MiniConsultButton from '../../components/MiniConsultButton';
import EnquireButton from '../../components/EnquireButton';
import { Phone, Smile, CheckCircle2, Clock, MapPin, Instagram, Facebook, Twitter, Music2, Sparkles, Award } from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

const CosmeticDentistActon = () => {
  const phoneNumber = '07787814064';
  const whatsappNumber = '447787814064';
  const phoneLink = `tel:${phoneNumber}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile
    ? `whatsapp://send?phone=${whatsappNumber}&text=Hi, I'd like to discuss cosmetic dentistry options`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=Hi, I'd like to discuss cosmetic dentistry options`;

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={cosmeticDentistActonPageSEO.title}
        description={cosmeticDentistActonPageSEO.description}
        path={cosmeticDentistActonPageSEO.path}
        keywords={cosmeticDentistActonPageSEO.keywords}
        image={cosmeticDentistActonPageSEO.image}
        structuredData={[
          getMedicalConditionLD(veneersCondition),
          getMedicalProcedureLD(veneersProcedure),
          getMedicalConditionLD(whiteningCondition),
          getMedicalProcedureLD(whiteningProcedure),
          getBreadcrumbLD(cosmeticDentistBreadcrumbs),
          getFAQPageLD(cosmeticDentistryFAQs),
        ]}
      />

      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
        <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <a href={phoneLink} className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
                <span className="font-semibold">07787 814 064</span>
              </a>
              <div className="flex items-center gap-3 md:gap-4">
                <a href="https://www.instagram.com/kartikpateldentist/" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Instagram"><Instagram className="w-5 h-5" /></a>
                <a href="https://www.facebook.com/profile.php?id=61588647254315" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Facebook"><Facebook className="w-5 h-5" /></a>
                <a href="https://twitter.com/restore_dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on X (Twitter)"><Twitter className="w-5 h-5" /></a>
                <a href="https://www.tiktok.com/@restore.dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on TikTok"><Music2 className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Smile className="w-8 h-8 text-burnt-coral" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/"><Button variant="outline" size="sm" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white">Home</Button></Link>
            <Button size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={whatsappLink}>Book Now</a></Button>
          </div>
        </div>
      </header>

      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-to-br from-[#01A9B4]/10 via-white to-[#006A71]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-[#006A71]/10 rounded-full border-2 border-[#01A9B4]/20">
              <p className="text-sm font-medium text-[#006A71]"><MapPin className="w-4 h-4 inline mr-1" />Acton, West London W3</p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-rabbit mb-6">Cosmetic Dentist in <span className="text-burnt-coral">Acton</span></h1>
            <p className="text-xl md:text-2xl text-chocolate-malt mb-8">Transform your smile with expert cosmetic dentistry</p>
            <DirectAnswer
              question="What does a smile makeover cost in Acton?"
              answer="Restore.Dental in Acton offers composite bonding from £250 per tooth, composite veneers £400–£600, porcelain veneers £800–£1,200 per tooth, and whitening from £350. Full smile makeovers range £3,500–£6,000 (composite) or £7,000–£12,000 (porcelain), planned with Digital Smile Design so you preview the result first. Led by Dr Kartik Patel BDS, GDC 177993."
              bullets={['Digital Smile Design preview', 'Bonding, veneers, whitening', '0% finance over 3–60 months', 'Natural, undetectable results']}
              className="max-w-3xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={whatsappLink}><WhatsAppIcon className="w-5 h-5 mr-2" />Free Consultation</a></Button>
              <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild><a href={phoneLink}><Phone className="w-5 h-5 mr-2" />Call Now</a></Button>
              <EnquireButton slug="cosmetic-dentistry" about="Cosmetic Dentistry" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="hero-enquire-btn" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-chocolate-malt">
              <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-burnt-coral" /><span>Complete Smile Makeovers</span></div>
              <div className="flex items-center gap-2"><Award className="w-5 h-5 text-burnt-coral" /><span>Expert Aesthetic Dentistry</span></div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-burnt-coral" /><span>Natural-Looking Results</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">Cosmetic Dentistry Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Sparkles className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Teeth Whitening</h3>
                  <p className="text-chocolate-malt">Professional whitening for a brighter, more confident smile</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Smile className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Composite Bonding</h3>
                  <p className="text-chocolate-malt">Fix chips, gaps & discoloration in one visit</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Award className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Porcelain Veneers</h3>
                  <p className="text-chocolate-malt">Transform your smile with custom-made veneers</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <CheckCircle2 className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Invisalign</h3>
                  <p className="text-chocolate-malt">Straighten teeth invisibly - 150+ cases completed</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-[#D4AF37]/10 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">Ready for Your Smile Transformation?</h2>
              <p className="text-lg text-chocolate-malt mb-6">Book a free cosmetic consultation to explore your options.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild><a href={whatsappLink}><WhatsAppIcon className="w-5 h-5 mr-2" />Book on WhatsApp</a></Button>
                <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild><a href={phoneLink}><Phone className="w-5 h-5 mr-2" />Call 07787 814 064</a></Button>
                <EnquireButton slug="cosmetic-dentistry" about="Cosmetic Dentistry" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="cta-enquire-btn" />
              </div>
            </div>

            <div className="mt-16">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">Individual Treatment Pages</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/invisalign-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Invisalign</h3><p className="text-sm text-chocolate-malt">Clear aligner treatment</p></Link>
                <Link to="/composite-bonding-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Composite Bonding</h3><p className="text-sm text-chocolate-malt">Same-day smile fix</p></Link>
                <Link to="/dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">General Dentistry</h3><p className="text-sm text-chocolate-malt">Complete dental care</p></Link>
                <Link to="/emergency-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all"><h3 className="font-bold text-rabbit mb-2">Emergency Dentist</h3><p className="text-sm text-chocolate-malt">Same-day emergency care</p></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Mini Consult Button */}
      <MiniConsultButton />
    </>
  );
};

export default CosmeticDentistActon;